import { SkeletonCards } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { CalendarDays, Clock3, Plus, RefreshCw, Search, UserRound, X } from 'lucide-react';
import { appointmentsAPI } from './appointmentsAPI';
import './appointments.css';

const TABS = [
  { key: 'requested', label: 'Requested' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'ongoing', label: 'Ongoing' },
  { key: 'completed', label: 'Completed' },
];

const EMPTY_FORM = { dealerId: '', emailid: '', mobile: '', regnum: '', description: '', service_type: 'repair', status: 'requested', scheduled_at: '' };
const SERVICES = [
  ['Repair', 'repair'], ['Service', 'service'], ['New Installation', 'New-installation'],
  ['Removal', 'removal'], ['Calibration', 'calibration'], ['Other', 'other'],
];

const getStoredUser = () => {
  for (const key of ['fleetuser', 'admin_user', 'alcotrackerparentuser', 'aargeeuser']) {
    try {
      const user = JSON.parse(localStorage.getItem(key));
      if (user) return user;
    } catch (_) { /* Try the next supported login record. */ }
  }
  return {};
};

const getList = (payload) => {
  if (Array.isArray(payload)) return payload;
  return [payload?.appointments, payload?.data, payload?.result, payload?.records].find(Array.isArray) || [];
};

const normalizeStatus = (value) => {
  const status = String(value || 'requested').toLowerCase().replace(/[\s_-]+/g, '');
  if (status.includes('complete') || status === 'done') return 'completed';
  if (status.includes('ongoing') || status.includes('progress') || status === 'active') return 'ongoing';
  if (status.includes('schedule') || status.includes('confirm')) return 'scheduled';
  return 'requested';
};

const valueFrom = (item, keys, fallback = '') => {
  const key = keys.find((candidate) => item?.[candidate] !== undefined && item?.[candidate] !== null);
  return key ? item[key] : fallback;
};

const normalizeAppointment = (item, index) => ({
  id: valueFrom(item, ['id', '_id', 'appointmentId', 'appointment_id'], index),
  clientName: valueFrom(item, ['dealerName', 'clientName', 'client_name', 'name'], 'Dealer'),
  phone: valueFrom(item, ['mobile', 'phone']),
  email: valueFrom(item, ['emailid', 'email']),
  date: valueFrom(item, ['scheduled_at', 'created_at']),
  time: valueFrom(item, ['scheduled_at', 'created_at']),
  serviceType: valueFrom(item, ['service_type', 'serviceType'], 'General appointment'),
  assignedTo: valueFrom(item, ['regnum', 'registrationNumber']),
  notes: valueFrom(item, ['description', 'notes']),
  status: normalizeStatus(valueFrom(item, ['status', 'appointmentStatus'])),
});

const formatDate = (value) => value && moment(value).isValid() ? moment(value).format('MMM D, YYYY') : 'Date not set';
const formatTime = (value) => value && moment(value).isValid() ? moment(value).format('h:mm A') : 'Time not set';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState('requested');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [dealerId, setDealerId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const appointmentsPerPage = 9;

  useEffect(() => {
    const user = getStoredUser();
    const id = user.dealerId || user.dealer_id || user.id || '';
    const email = user.emailId || user.emailid || user.email || '';
    setDealerId(id);
    setForm((current) => ({ ...current, dealerId: id, emailid: email }));
    if (!id) {
      setLoading(false);
      setError('Dealer ID is missing for the logged-in user.');
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError('');
    try {
      const response = await appointmentsAPI.getAppointments(dealerId);
      setAppointments(getList(response).map(normalizeAppointment));
    } catch (requestError) {
      setError(requestError.message || 'Unable to load appointments.');
    } finally { setLoading(false); }
  }, [dealerId]);

  useEffect(() => {
    if (!dealerId) return;
    loadAppointments();
    appointmentsAPI.getDealerVehicles(dealerId).then((response) => {
      setVehicles(getList(response).map((item) => ({
        regnum: item?.Vehicle?.regnum || item?.regnum || '',
        phone: item?.Vehicle?.clientphone1 || item?.mobile || '',
      })).filter((item) => item.regnum));
    }).catch(() => setVehicles([]));
  }, [dealerId, loadAppointments]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => setNotice(''), 3500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const counts = useMemo(() => TABS.reduce((result, tab) => ({ ...result, [tab.key]: appointments.filter((item) => item.status === tab.key).length }), {}), [appointments]);
  const visibleAppointments = useMemo(() => {
    const query = search.trim().toLowerCase();
    return appointments.filter((item) => item.status === activeTab && (!query || [item.clientName, item.phone, item.email, item.serviceType, item.assignedTo, item.notes].some((value) => String(value || '').toLowerCase().includes(query))));
  }, [activeTab, appointments, search]);
  const totalPages = Math.max(1, Math.ceil(visibleAppointments.length / appointmentsPerPage));
  const paginatedAppointments = useMemo(() => {
    const start = (currentPage - 1) * appointmentsPerPage;
    return visibleAppointments.slice(start, start + appointmentsPerPage);
  }, [currentPage, visibleAppointments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const resetForm = () => {
    const user = getStoredUser();
    setForm({ ...EMPTY_FORM, dealerId, emailid: user.emailId || user.emailid || user.email || '' });
  };
  const closeModal = () => { if (!saving) { setIsModalOpen(false); resetForm(); } };
  const handleChange = ({ target: { name, value } }) => setForm((current) => {
    if (name === 'regnum') return { ...current, regnum: value, mobile: vehicles.find((item) => item.regnum === value)?.phone || '' };
    return { ...current, [name]: value };
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true); setError('');
    try {
      await appointmentsAPI.addAppointment({ ...form, scheduled_at: moment(form.scheduled_at).format('MM/DD/YYYY HH:mm:ss'), created_at: moment().format('MM/DD/YYYY HH:mm:ss') });
      setActiveTab('requested'); setIsModalOpen(false); resetForm(); setNotice('Appointment added successfully.');
      await loadAppointments();
    } catch (requestError) { setError(requestError.message || 'Unable to add the appointment.'); }
    finally { setSaving(false); }
  };

  return (
    <section className="appointments-page">
      <header className="appointments-header">
        <div><span className="appointments-eyebrow">Schedule management</span><h1>Appointments</h1>
        {/* <p>Review requests and keep every appointment moving forward.</p> */}
        </div>
        <button className="appointments-add-btn" onClick={() => setIsModalOpen(true)}><Plus size={18} /> Add Appointment</button>
      </header>
      {notice && <div className="appointments-notice" role="status">{notice}</div>}
      {error && <div className="appointments-error" role="alert">{error}</div>}
      <div className="appointments-panel">
        <div className="appointments-toolbar">
          <div className="appointments-tabs" role="tablist" aria-label="Appointment status">
            {TABS.map((tab) => <button key={tab.key} className={`appointments-tab ${activeTab === tab.key ? 'is-active' : ''}`} onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }} role="tab" aria-selected={activeTab === tab.key}>{tab.label}<span>{counts[tab.key] || 0}</span></button>)}
          </div>
          <div className="appointments-actions">
            <label className="appointments-search"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search appointments" /></label>
            <button className="appointments-refresh" onClick={loadAppointments} disabled={loading} aria-label="Refresh appointments"><RefreshCw size={18} className={loading ? 'is-spinning' : ''} /></button>
          </div>
        </div>
        {loading ? <SkeletonCards className="appointments-grid" itemClassName="appointment-card" label="Loading appointments..." /> : counts[activeTab] === 0 || visibleAppointments.length === 0 ? (
          <div className="appointments-state"><div className="appointments-empty-icon"><CalendarDays size={30} /></div><h3>No {activeTab} appointments</h3><p>{search ? 'Try a different search.' : 'New appointments will appear here.'}</p></div>
        ) : <><div className="appointments-grid">{paginatedAppointments.map((appointment) => (
          <article className="appointment-card" key={appointment.id}>
            <div className="appointment-card-top"><span className={`appointment-status status-${appointment.status}`}>{appointment.status}</span>
            </div>
            <h3>{appointment.clientName}</h3><p className="appointment-service">{appointment.serviceType}{appointment.notes ? ` - ${appointment.notes}` : ''}</p>
            <div className="appointment-meta"><span><CalendarDays size={16} />{formatDate(appointment.date)}</span><span><Clock3 size={16} />{formatTime(appointment.time)}</span>{appointment.assignedTo && <span><UserRound size={16} />{appointment.assignedTo}</span>}</div>
            {(appointment.phone || appointment.email) && <div className="appointment-contact">{appointment.phone || appointment.email}</div>}
          </article>
        ))}</div>
        {totalPages > 1 && <div className="appointments-pagination">
          <span>Showing {(currentPage - 1) * appointmentsPerPage + 1}–{Math.min(currentPage * appointmentsPerPage, visibleAppointments.length)} of {visibleAppointments.length}</span>
          <div>
            <button onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>Previous</button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1).map((page, index, pages) => <React.Fragment key={page}>{index > 0 && page - pages[index - 1] > 1 && <i>…</i>}<button className={page === currentPage ? 'is-active' : ''} onClick={() => setCurrentPage(page)}>{page}</button></React.Fragment>)}
            <button onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
        </div>}</>}
      </div>
      {isModalOpen && <div className="appointment-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && closeModal()}>
        <div className="appointment-modal" role="dialog" aria-modal="true" aria-labelledby="appointment-modal-title">
          <div className="appointment-modal-header"><div><span className="appointments-eyebrow">New booking</span><h2 id="appointment-modal-title">Add Appointment</h2></div><button type="button" onClick={closeModal} aria-label="Close"><X size={20} /></button></div>
          <form onSubmit={handleSubmit}><div className="appointment-form-grid">
            <label>Vehicle<select name="regnum" value={form.regnum} onChange={handleChange} required><option value="">Select vehicle</option>{vehicles.map((vehicle) => <option key={vehicle.regnum} value={vehicle.regnum}>{vehicle.regnum}</option>)}</select></label>
            <label>Phone<input name="mobile" value={form.mobile} readOnly placeholder="Vehicle phone number" /></label>
            <label className="wide">Email<input name="emailid" type="email" value={form.emailid} onChange={handleChange} placeholder="dealer@example.com" /></label>
            <label>Service type<select name="service_type" value={form.service_type} onChange={handleChange}>{SERVICES.map(([label, value]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label>Scheduled date and time<input name="scheduled_at" type="datetime-local" value={form.scheduled_at} onChange={handleChange} required /></label>
            <label className="wide">Description<textarea name="description" value={form.description} onChange={handleChange} rows="3" required placeholder="Add appointment details" /></label>
          </div><div className="appointment-modal-footer"><button type="button" className="appointment-cancel-btn" onClick={closeModal}>Cancel</button><button type="submit" className="appointments-add-btn" disabled={saving}>{saving ? 'Saving...' : 'Add Appointment'}</button></div></form>
        </div>
      </div>}
    </section>
  );
};

export default Appointments;
